import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ListViewModeSwitcher, { listViewModes } from '../list-view-mode-switcher';
import ResourcesList from '../resources-list';
import ThumbList from '../thumb-list';
import TableList from '../table-list';
import ResourceType from '../../types/resource';
import CrumbType from '../../types/crumb';
import BreadCrumbs from '../bread-crumbs';
import SortTypeSwitcher, { sortMap, optionsKeys } from "../sort-type-switcher";
import EmptyList from '../empty-list';

import './resource-dialog.css';

export const listViewComponents = {
  [listViewModes.THUMB]: ThumbList,
  [listViewModes.TABLE]: TableList
};

export const hiddenStyles = { display: 'none' };

class ResourceDialog extends PureComponent
{
  static propTypes = {
    accept: PropTypes.arrayOf(PropTypes.string),
    isLoading: PropTypes.bool,
    isInsertDocumentMode: PropTypes.bool.isRequired,
    keepOriginalSize: PropTypes.bool,
    insertValidation: PropTypes.func,
    onInsert: PropTypes.func.isRequired,
    onChangeKeepOriginalSize: PropTypes.func.isRequired,
    onDropFiles: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChangeLocation: PropTypes.func.isRequired,
    currentResources: PropTypes.arrayOf(ResourceType).isRequired,
    location: PropTypes.arrayOf(CrumbType).isRequired
  };

  static defaultProps = {
    isLoading: true,
    keepOriginalSize: false,
    insertValidation: () => true
  };

  static getDerivedStateFromProps(nextProps, prevState)
  {
    const { sortType } = prevState;
    let { selectedResource } = prevState;
    const { currentResources, defaultSelectedIndex } = nextProps;

    if (currentResources) {

      if (defaultSelectedIndex)
      {
        selectedResource = currentResources[defaultSelectedIndex];
      }

      const resources = ([...currentResources]).sort(sortMap[sortType]);

      return {
        ...prevState,
        selectedResource,
        resources
      };
    }

    return {
      ...prevState,
      selectedResource,
      resources: currentResources
    };
  }

  constructor(props)
  {
    super(props);

    const { isInsertDocumentMode } = props;

    this.state = {
      listViewMode: isInsertDocumentMode ? listViewModes.TABLE : listViewModes.THUMB,
      selectedResource: null,
      sortType: optionsKeys[0],
      resources: props.currentResources
    };

    this.fileInput = React.createRef();
  }

  handleChangeListViewMode = (listViewMode) =>
  {
    this.setState({ listViewMode });
  };

  handleSelect = (selectedResource) =>
  {
    this.setState({ selectedResource })
  };

  handChangeLocation = (item) =>
  {
    this.props.onChangeLocation(item);
  };

  handleChangeOrder = (sortType) =>
  {
    this.setState({ sortType });
  };

  handleInsert = () =>
  {
    const { fullPath, name } = this.state.selectedResource;
    this.props.onInsert({ fullPath, name });
  };

  handleInsertFromList = ({ fullPath, name }) =>
  {
    this.props.onInsert({ fullPath, name });
  };

  handleCancel = () =>
  {
    this.props.onCancel();
  };

  handleChangeLocationByBreadCrumbs = (href) =>
  {
    this.props.onChangeLocation({ href })
  };

  handleChangeFile = ({ target: { files } }) =>
  {
    const { onDropFiles } = this.props;
    onDropFiles(files);
  };

  handleChooseFile = (e) =>
  {
    e.preventDefault();
    this.fileInput.current.click();
  };

  handleChangeKeepOriginalSize = () =>
  {
    this.props.onChangeKeepOriginalSize(!this.props.keepOriginalSize);
  };

  render()
  {
    const {
      location,
      isLoading,
      keepOriginalSize,
      insertValidation
    } = this.props;

    const { listViewMode, resources, selectedResource } = this.state;

    const CurrentListViewComponent = listViewComponents[listViewMode];
    const isEmpty = (resources && resources.length === 0);
    const isInitial = !resources;

    const contentClass = classnames({
      'resource-dialog__content': true,
      'resource-dialog__content_loading': isLoading,
      'resource-dialog__content_empty': isEmpty
    });

    const resourceDialogClasses = classnames({
      'resource-dialog': true,
      'resource-dialog_loading': isLoading
    });

    const browseButtonClasses = classnames({
      'resource-dialog__browse-button': true,
      'resource-dialog__button': true,
      'resource-dialog__button_disabled': isLoading,
      'resource-dialog__browse-button_disabled': isLoading
    });

    const isCanInsert = selectedResource
      && !selectedResource.isDirectory
      && insertValidation(selectedResource);

    const insertButtonClass = classnames({
      'resource-dialog__button': true,
      'resource-dialog__button-insert': true,
      'resource-dialog__button_green': true,
      'resource-dialog__button_disabled': !isCanInsert
    });

    return (
      <div className={resourceDialogClasses}>
        <input
          accept={this.props.accept.join && this.props.accept.join(',')}
          type="file"
          name="insertResourceDialogFileInput"
          onChange={this.handleChangeFile}
          style={hiddenStyles}
          ref={this.fileInput}
          multiple
        />
        <div className="resource-dialog__wrapper">
          <div className="resource-dialog__close-button" />
          <div className="resource-dialog__rounded-box">
            <div className="resource-dialog__container">
              <div className="resource-dialog__header">
                <div className="resource-dialog__main-toolbar-container">
                  <div className="resource-dialog__main-toolbar">
                    <div className="resource-dialog__main-toolbar_left-container">
                      <div className="resource-dialog__main-toolbar_left">
                        <BreadCrumbs
                          location={location}
                          onChangeLocation={this.handleChangeLocationByBreadCrumbs}
                        />
                      </div>
                    </div>
                    <div className="resource-dialog__main-toolbar_right-container">
                      <div className="resource-dialog__main-toolbar_right">
                        <div
                          className="resource-dialog__edit-folder-buttons-container"
                          style={hiddenStyles}
                        >
                          <div className="resource-dialog__add-folder-button" />
                          <div className="resource-dialog__edit-folder-button" />
                        </div>
                        <div className="resource-dialog__toolbar-divider" />
                        <ListViewModeSwitcher
                          value={this.state.listViewMode}
                          onChange={this.handleChangeListViewMode}
                          isDisabled={isLoading}
                        />
                        <SortTypeSwitcher
                          onChange={this.handleChangeOrder}
                          isDisabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="resource-dialog__second-toolbar-container">
                  <div className="resource-dialog__second-toolbar">
                    <div className="resource-dialog__image-upload-toolbar">
                      <div className="resource-dialog__image-upload-toolbar-left">
                        <div className="resource-dialog__image-upload-toolbar-description">
                          <span>To upload images </span>
                          {!isLoading && (<a
                              href="#"
                              className="resource-dialog__link"
                              onClick={this.handleChooseFile}
                            >browse</a>)}
                          {isLoading && (<span>browse</span>)}
                          <span> your computer.</span>
                        </div>
                        <div
                          className="resource-dialog__keep-original-control"
                          style={hiddenStyles}
                        >
                          <input
                            type="checkbox"
                            checked={keepOriginalSize}
                            onClick={this.handleChangeKeepOriginalSize}
                            id="keep-original-control"
                            className="resource-dialog__keep-original-control-input"
                            disabled={isLoading}
                          />
                          <label
                            htmlFor="keep-original-control"
                            className="resource-dialog__keep-original-control-label"
                          >
                            Keep original image size
                          </label>
                        </div>
                      </div>
                      <div className="resource-dialog__image-upload-toolbar-right">
                        <button
                          className={browseButtonClasses}
                          disabled={isLoading}
                          onClick={this.handleChooseFile}
                          data-test-id="idIRD_BrowseButton"
                        >
                          Browse
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="resource-dialog__content-header">
            </div>
            <div className={contentClass}>
              <div className="resource-dialog__content-loading">
                <div className="resource-dialog__content-loading-icon" />
                <div className="resource-dialog__content-loading-label">Please wait ...</div>
              </div>
              {!isInitial && isEmpty && <EmptyList />}
              {!isInitial && !isEmpty && <ResourcesList
                selectedResource={selectedResource}
                resources={resources}
                insertValidation={insertValidation}
                onInsert={this.handleInsertFromList}
                onSelect={this.handleSelect}
                onChangeLocation={this.handChangeLocation}
              >
                {props => (<CurrentListViewComponent
                  {...props}
                  insertValidation={insertValidation}
                />)}
              </ResourcesList>}
            </div>
            <div className="resource-dialog__footer">
              <button
                className={insertButtonClass}
                onClick={this.handleInsert}
                disabled={!isCanInsert}
              >
                Insert
              </button>
              <button
                data-test-id="idIRD_InsertButton"
                className="resource-dialog__button resource-dialog__button-insert"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ResourceDialog